/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { InfoBox } from '@THC/components/InfoBox';
import T from 'i18n-react';
import Link from 'next/link';
import Router from 'next/router';

import { FeedbackTooltip, withViewportProvider } from '@motorway/motorway-component-library';
import { Button } from '@motorway/mw-highway-code';
import { Hyperlink } from '@motorway/mw-highway-code/alpha';

import cypressIds from 'Components/cypress_ids.json';
import { MOTORWAY_PAY_UNAUTHORISED_PARAM } from 'Components/StaticPages/DiscoverMWPay/DiscoverMWPay.consts';
import ErrorPage from 'Components/StaticPages/StaticContents/Error/Error';
import { AppContext } from 'Context/AppContext';
import { triggerSignInClickedEvent } from 'Services/analytics/events';
import { signIn } from 'Services/api/user';
import { logger } from 'Services/logger/logger';
import Content from 'Storybook/local/Content/Content';
import FormBase from 'Utilities/FormBase/FormBase';
import { applyCypressData, isUndefined } from 'Utilities/helpers';
import { signInRedirect } from 'Utilities/helpers/signin';
import {
	createSignInMessage,
	windowCommunicationChannel,
} from 'Utilities/windowCommunicationChannel/windowCommunicationChannel.helpers';

import { ERRORCODE } from '../../server/consts';

import styles from './SignIn.module.scss';

T.setTexts(require('../texts.json'));

const VIEWS = Object.freeze({
	ERROR: Symbol('ERROR'),
	SIGNIN: Symbol('SIGNIN'),
});

const fields = ['username', 'password', 'remember'];

const defaultValues = {
	remember: {
		required: false,
		value: true,
	},
};

class SignIn extends FormBase {
	constructor(props) {
		super(props);

		this.INITIAL_STATE = {
			errorCode: '',
			inputs: fields.map((f) => ({
				focused: false,
				name: f,
				required: true,
				showIfInvalid: false,
				valid: true,
				value: '',
				...defaultValues[f],
			})),
			loading: false,
			notFound: false,
			view: VIEWS.SIGNIN,
		};

		this.state = this.INITIAL_STATE;

		this.$form = React.createRef();
		this.textsPrefix = 'signIn';
	}

	get isOnSidebar() {
		const { mode } = this.props;

		return mode === 'sidebar';
	}

	// eslint-disable-next-line class-methods-use-this
	customCheckValidity = (id, valid) => {
		if (id === 'remember') {
			return true;
		}

		return valid;
	};

	customErrorMessage = (id) => {
		let name = id;
		if (id === 'username' && !this.checkValidity(id) && this.getValue(id)) {
			name = 'username:invalid';
		}

		if (id === 'notFound') {
			name = 'unauthorized:default';

			if (this.state.errorCode === ERRORCODE.UNAUTHORIZED_DEACTIVATED) {
				const text = T.translate(`${this.textsPrefix}.errorMessages.unauthorized:deactivated`);
				const phoneNumber = T.translate('contactInfo.phone');
				return (
					<>
						{text}
						<T.a href={`tel:${phoneNumber.replace(/ /g, '')}`} text={{ key: 'contactInfo.phone' }} />.
					</>
				);
			}

			if (this.state.errorCode === ERRORCODE.UNAUTHORIZED_RATE_LIMITED) {
				return 'unauthorized:rate-limited';
			}
		}

		return name;
	};

	getNotFound = () => (
		<FeedbackTooltip
			fillWidth
			tooltipWithinParent
			appendTo={this.$form.current}
			content={this.getErrorMessage('notFound', {
				spacer: ' ',
			})}
			intent="error"
			maxWidth={400}
			placement="top"
		>
			<div className={styles.notFound} />
		</FeedbackTooltip>
	);

	getSignInView = () => {
		const { query } = this.props;
		const { loading, notFound } = this.state;

		const unauthorised = isUndefined(query.unauthorised) ? false : query.unauthorised || query.unauthorised === '';
		const unauthorisedMotorwayPay = unauthorised && query.unauthorised === MOTORWAY_PAY_UNAUTHORISED_PARAM;
		const email = T.translate('contactInfo.email');
		const phone = T.translate('contactInfo.phone');

		const signInFormTitle = T.translate(
			this.isOnSidebar ? 'vehicleDetails.signIn.title' : `${this.textsPrefix}.formTitle`,
		);

		return (
			<>
				{unauthorised && (
					<div className={styles.infoBoxWrapper}>
						<InfoBox
							content={
								<>
									<span className={cx(styles.signUpMessage, styles.signInMessage)}>
										{T.translate(`${this.textsPrefix}.unauthorised.message`, {
											signUp: (
												<Hyperlink
													as="a"
													href="/signup"
													label={T.translate(`${this.textsPrefix}.unauthorised.signUp`)}
												/>
											),
										})}
									</span>
									<span className={styles.signInMessage}>
										{T.translate(`${this.textsPrefix}.unauthorised.info`, {
											email: <Hyperlink as="a" href={`mailto:${email}`} label={email} />,
											phone: <Hyperlink as="a" href={`tel:${phone}`} label={phone} />,
										})}
									</span>
								</>
							}
							heading={
								unauthorisedMotorwayPay
									? T.translate(`${this.textsPrefix}.unauthorisedMotorwayPay.title`)
									: T.translate(`${this.textsPrefix}.unauthorised.title`)
							}
							variant="info"
						/>
					</div>
				)}
				<h1>{signInFormTitle}</h1>
				<form ref={this.$form} noValidate className={styles.container} onSubmit={this.onSubmit}>
					{notFound && this.getNotFound()}
					{this.getTextInput({
						inputProps: {
							autoComplete: 'username',
							...applyCypressData(cypressIds.inputs.username),
							name: 'username',
							pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+[.][a-zA-Z]{2,}$',
						},
						label: T.translate(`${this.textsPrefix}.username`),
						name: 'username',
						type: 'email',
					})}
					{this.getTextInput({
						inputProps: {
							autoComplete: 'current-password',
							name: 'password',
							...applyCypressData(cypressIds.inputs.password),
						},
						label: T.translate(`${this.textsPrefix}.password`),
						name: 'password',
						type: 'password',
					})}
					{this.getCheckboxInput({
						inputProps: {
							...applyCypressData(cypressIds.inputs.rememberPassword),
						},
						label: T.translate(`${this.textsPrefix}.remember`),
						name: 'remember',
					})}
					<Button
						{...applyCypressData(cypressIds.buttons.submit)}
						fullWidth
						disabled={loading}
						icon={this.isOnSidebar ? 'chevron' : undefined}
						label={T.translate(`${this.textsPrefix}.button`)}
						loading={loading}
						type="submit"
					/>
					<div className={styles.spacer} />
					<Link legacyBehavior passHref as="/forgot-password" href="/forgot_password">
						<Hyperlink
							as="a"
							{...applyCypressData(cypressIds.links.forgotPassword)}
							label={T.translate(`${this.textsPrefix}.forgot`)}
						/>
					</Link>
					<div className={styles.spacer} />
					<p className={styles.signUpCopy}>
						{T.translate(`${this.textsPrefix}.signUp`)}{' '}
						<Link legacyBehavior passHref as="/signup" href="/signup">
							<Hyperlink
								as="a"
								{...applyCypressData(cypressIds.links.signUp)}
								label={T.translate(`${this.textsPrefix}.signUpLink`)}
							/>
						</Link>
						.
					</p>
				</form>
			</>
		);
	};

	getViewContext = () => {
		const { view } = this.state;

		switch (view) {
			case VIEWS.ERROR:
				return <ErrorPage />;
			default:
				return this.getSignInView();
		}
	};

	submit = async () => {
		const { asPath, useRedirect } = this.props;
		const body = {};

		this.state.inputs.forEach((i) => {
			body[i.name] = this.getInputFormat(i.name, i.value);
		});

		this.setState({
			loading: true,
			notFound: false,
		});

		try {
			await signIn({
				body,
			});

			triggerSignInClickedEvent();
			windowCommunicationChannel.postMessage(createSignInMessage());
			if (useRedirect) {
				signInRedirect(asPath);
				return;
			}
			Router.reload();
		} catch (error) {
			const { response: res } = error;
			if (res && res.status === 401) {
				fields.forEach((id) => this.updateInputState(id, { showIfInvalid: false, valid: false }));

				this.setState({
					errorCode: res.data?.errorCode || '',
					loading: false,
					notFound: true,
				});
			} else {
				this.resetForm({
					loading: false,
					view: VIEWS.ERROR,
				});
				logger.error({
					error,
					message: error?.message || 'There was an issue with sign in',
					scope: 'SignIn submit',
				});
			}
		}
	};

	componentDidUpdate = () => {
		if (this.state.view !== VIEWS.SIGNIN) {
			window.scrollTo(0, 0);
		}
	};

	componentDidMount = () => {
		let view = VIEWS.SIGNIN;

		if (this.props.view === 'error') {
			view = VIEWS.ERROR;
		}

		this.setState({ view });
	};

	render() {
		return (
			<div className={cx(styles.root, { [styles.onSidebar]: this.isOnSidebar })}>
				<Content className={cx(styles.content)}>{this.getViewContext()}</Content>
			</div>
		);
	}
}

SignIn.contextType = AppContext;

SignIn.defaultProps = {
	mode: 'page',
	query: {},
	useRedirect: true,
};

SignIn.propTypes = {
	mode: PropTypes.oneOf(['sidebar', 'page']),
	query: PropTypes.shape({}),
	useRedirect: PropTypes.bool,
	view: PropTypes.oneOf(['loader', 'error']),
};

export default withViewportProvider(SignIn);
